<template>
  <v-card>
    <v-card-title class="primary white--text headline">
      <span>{{ title }}</span>
      <v-spacer/>
      <BaseDialogActions hideRefresh @close="askForConfirmation"/>
    </v-card-title>
    <v-card-text>
      <v-container fluid v-if="!importingInProgress">
        <FormWrapper
          :buttonText="$t('confirm')"
          @submit="confirmButtonClicked"
          :disabled="reviewConfirmed"
        >
          <v-row dense align="center">
            <v-col>
              <v-text-field
                style="min-width: 128px;"
                v-model="search"
                append-icon="mdi-magnify"
                :label="$t('search')"
                single-line
                clearable
                color="primary"
              />
            </v-col>
            <v-col cols="auto">
              <v-row dense align="center" v-if="currentTab === 0 && logsImportData.length > 0">
                <v-col cols="auto" v-for="(key, index) in groupingIcons" :key="index" >
                  <Icon
                    :icon="key.icon"
                    :small="false"
                    :tooltipText="$t(key.name)"
                    @icon-clicked="handleGroupChange(key.value)"
                    margin="mr-1"
                    :iconColor="key.active ? 'primary' : 'grey darken-2'"
                  />
                </v-col>
              </v-row>
              <v-row dense align="center" v-if="currentTab === 1 || (currentTab === 0 && logsImportData.length === 0)">
                <v-col cols="auto" v-for="(key, index) in lysGroupingIcons" :key="index">
                  <Icon
                    :icon="key.icon"
                    :small="false"
                    :tooltipText="$t(key.name)"
                    @icon-clicked="handleGroupChangeLYS(key.value)"
                    margin="mr-1"
                    :iconColor="key.active ? 'primary' : 'grey darken-2'"
                  />
                </v-col>
              </v-row>
            </v-col>
            <v-col cols="12">
              <v-tabs v-model="currentTab" class="mb-2">
                <v-tab v-if="logsImportData.length > 0">{{ $t('logs') }}</v-tab>
                <v-tab v-if="logYardSaleImportData.length > 0">{{ $t('logYardSale') }}</v-tab>
              </v-tabs>
              <v-tabs-items v-model="currentTab">
                <!-- Logs Tab -->
                <v-tab-item v-if="logsImportData.length > 0">
                  <v-row dense align="center" class="mt-1">
                    <v-col cols="12">
                      <v-expansion-panels
                        v-if="groupByDestination || groupByTract || groupByAccount || groupByContract || groupBySetting"
                        v-model="openDestinationPanel"
                        accordion
                        class="mb-3">
                        <v-expansion-panel v-for="(group, index) in logsImportDataGroupedBySelection" :key="index">
                          <v-expansion-panel-header>
                            <span class="text-h6" v-if="!groupByContract">
                              {{ group[0] }}
                            </span>
                            <span class="text-h6" v-else>
                              <v-row dense no-gutters>
                                <v-col cols="6" v-for="(field, i) in contractKeys" :key="i">
                                  <Icon
                                    :icon="field.icon"
                                    :tooltipText="$t(field.name)"
                                    iconColor="grey"
                                    propagateClick
                                  />
                                  <span>
                                    {{group[1][0][field.name] ?? 'N/A'}}
                                  </span>
                                </v-col>
                              </v-row>
                            </span>
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <DataTable
                              :items.sync="group[1]"
                              :headers="columnSetBySelection"
                              :customCells="customColumnsBySelection"
                              class="pt-2"
                            >
                              <template #extTicketNumber2="{ item }">
                                <span>{{ item.extTicketNumber2 }}</span>
                              </template>
                              <template #tract="{ item }">
                                <span>{{ item.tract }}</span>
                              </template>
                              <template #destination="{ item }">
                                <span>{{ item.destination }}</span>
                              </template>
                              <template #setting="{ item }">
                                <span>{{ item.setting }}</span>
                              </template>
                              <template #netWeight="{ item }">
                                <span class="text-right">{{ item.netWeight }}</span>
                              </template>
                              <template #actions="{ item }">
                                <Icon
                                  v-if="item.ticketType.value === ScaleSheetTicketType.IncludesVolume.value"
                                  icon="mdi-ruler-square"
                                  :tooltipText="$t('viewScaleData')"
                                  @icon-clicked="showScaleData(item)"
                                  :small="false"
                                  :iconColor="item.reviewed ? 'grey darken-2' : 'primary'"
                                />
                                <Icon
                                  v-else-if="item.ticketType.value === ScaleSheetTicketType.WeightBased.value"
                                  icon="mdi-weight"
                                  :tooltipText="$t('weightBased')"
                                  :small="false"
                                  iconColor="grey darken-2"
                                />
                                <Icon
                                  v-else
                                  icon="mdi-alpha-e-circle-outline"
                                  :tooltipText="$t('expandedTicketNoScaleData')"
                                  :small="false"
                                  iconColor="grey darken-2"
                                />
                              </template>
                            </DataTable>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </v-col>
                    <v-col cols="12">
                      <DataTable
                        v-if="!groupByDestination && !groupByTract && !groupByAccount && !groupByContract && !groupBySetting"
                        :items.sync="logsImportData"
                        :headers="columnSet"
                        :customCells="customColumns"
                        :externalSearch="search"
                        :showSearch="false"
                        :enableExternalSearch="true"
                        class="mt-n2"
                      >
                        <template #extTicketNumber2="{ item }">
                          <span>{{ item.extTicketNumber2 }}</span>
                        </template>
                        <template #tract="{ item }">
                          <span>{{ item.tract }}</span>
                        </template>
                        <template #destination="{ item }">
                          <span>{{ item.destination }}</span>
                        </template>
                        <template #setting="{ item }">
                          <span>{{ item.setting }}</span>
                        </template>
                        <template #netWeight="{ item }">
                          <span class="text-right">{{ item.netWeight }}</span>
                        </template>
                        <template #actions="{ item }">
                          <Icon
                            v-if="item.ticketType.value === ScaleSheetTicketType.IncludesVolume.value"
                            icon="mdi-ruler-square"
                            :tooltipText="$t('viewScaleData')"
                            @icon-clicked="showScaleData(item)"
                            :small="false"
                            :iconColor="item.reviewed ? 'grey darken-2' : 'primary'"
                          />
                          <Icon
                            v-else-if="item.ticketType.value === ScaleSheetTicketType.WeightBased.value"
                            icon="mdi-weight"
                            :tooltipText="$t('weightBased')"
                            :small="false"
                            iconColor="grey darken-2"
                          />
                          <Icon
                            v-else
                            icon="mdi-information-outline"
                            :tooltipText="$t('expandedTicketNoScaleData')"
                            :small="false"
                            iconColor="grey darken-2"
                          />
                        </template>
                      </DataTable>
                    </v-col>
                  </v-row>
                </v-tab-item>
                <!-- Log Yard Sale Tab -->
                <v-tab-item v-if="logYardSaleImportData.length > 0">
                  <v-row dense align="center" class="mt-1">
                    <v-col cols="12">
                      <v-expansion-panels
                        v-if="lysGroupByFromAccount || lysGroupByDestination || lysGroupByAccount || lysGroupByContract"
                        v-model="openLYSPanel"
                        accordion
                        class="mb-3">
                        <v-expansion-panel v-for="(group, index) in lysImportDataGroupedBySelection" :key="index">
                          <v-expansion-panel-header>
                            <span class="text-h6" v-if="!lysGroupByContract">
                              {{ group[0] }}
                            </span>
                            <span class="text-h6" v-else>
                              <v-row dense no-gutters>
                                <v-col cols="6" v-for="(field, i) in lysContractKeys" :key="i">
                                  <Icon
                                    :icon="field.icon"
                                    :tooltipText="$t(field.name)"
                                    iconColor="grey"
                                    propagateClick
                                  />
                                  <span>
                                    {{group[1][0][field.name] ?? 'N/A'}}
                                  </span>
                                </v-col>
                              </v-row>
                            </span>
                          </v-expansion-panel-header>
                          <v-expansion-panel-content>
                            <DataTable
                              :items.sync="group[1]"
                              :headers="columnSetLYSBySelection"
                              :customCells="customColumnsBySelectionLYS"
                              class="pt-2"
                            >
                              <template #ticketNumber="{ item }">
                                <span>{{ item.ticketNumber }}</span>
                              </template>
                              <template #fromAccount="{ item }">
                                <span>{{ item.fromAccount }}</span>
                              </template>
                              <template #account="{ item }">
                                <span>{{ item.account }}</span>
                              </template>
                              <template #destination="{ item }">
                                <span>{{ item.destination }}</span>
                              </template>
                              <template #netWeight="{ item }">
                                <span class="text-right">{{ item.netWeight }}</span>
                              </template>
                              <template #actions="{ item }">
                                <Icon
                                  v-if="item.ticketType.value === ScaleSheetTicketType.IncludesVolume.value"
                                  icon="mdi-ruler-square"
                                  :tooltipText="$t('viewScaleData')"
                                  @icon-clicked="showScaleData(item)"
                                  :small="false"
                                  :iconColor="item.reviewed ? 'grey darken-2' : 'primary'"
                                />
                                <Icon
                                  v-else-if="item.ticketType.value === ScaleSheetTicketType.WeightBased.value"
                                  icon="mdi-weight"
                                  :tooltipText="$t('weightBased')"
                                  :small="false"
                                  iconColor="grey darken-2"
                                />
                                <Icon
                                  v-else
                                  icon="mdi-information-outline"
                                  :tooltipText="$t('expandedTicketNoScaleData')"
                                  :small="false"
                                  iconColor="grey darken-2"
                                />
                              </template>
                            </DataTable>
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                    </v-col>
                    <v-col cols="12">
                      <DataTable
                        v-if="!lysGroupByFromAccount && !lysGroupByDestination && !lysGroupByAccount && !lysGroupByContract"
                        :items.sync="logYardSaleImportData"
                        :headers="columnSetLYS"
                        :customCells="customColumnsLYS"
                        :externalSearch="search"
                        :showSearch="false"
                        :enableExternalSearch="true"
                        class="mt-n2"
                      >
                        <template #ticketNumber="{ item }">
                          <span>{{ item.ticketNumber }}</span>
                        </template>
                        <template #fromAccount="{ item }">
                          <span>{{ item.fromAccount }}</span>
                        </template>
                        <template #account="{ item }">
                          <span>{{ item.account }}</span>
                        </template>
                        <template #destination="{ item }">
                          <span>{{ item.destination }}</span>
                        </template>
                        <template #netWeight="{ item }">
                          <span class="text-right">{{ item.netWeight }}</span>
                        </template>
                        <template #actions="{ item }">
                          <Icon
                            v-if="item.ticketType.value === ScaleSheetTicketType.IncludesVolume.value"
                            icon="mdi-ruler-square"
                            :tooltipText="$t('viewScaleData')"
                            @icon-clicked="showScaleData(item)"
                            :small="false"
                            :iconColor="item.reviewed ? 'grey darken-2' : 'primary'"
                          />
                          <Icon
                            v-else-if="item.ticketType.value === ScaleSheetTicketType.WeightBased.value"
                            icon="mdi-weight"
                            :tooltipText="$t('weightBased')"
                            :small="false"
                            iconColor="grey darken-2"
                          />
                          <Icon
                            v-else
                            icon="mdi-information-outline"
                            :tooltipText="$t('expandedTicketNoScaleData')"
                            :small="false"
                            iconColor="grey darken-2"
                          />
                        </template>
                      </DataTable>
                    </v-col>
                  </v-row>
                </v-tab-item>
              </v-tabs-items>
            </v-col>
          </v-row>
        </FormWrapper>
      </v-container>
      <v-container fluid v-else>
        <v-row justify="center" class="mt-5">
          <v-col cols="auto" class="text-center">
            <v-progress-linear
              color="primary"
              :value="importPercentage"
              height="16"
              style="width: 300px"
             ></v-progress-linear>
            <div class="mt-2">{{ importPercentage }}%</div>
          </v-col>
        </v-row>
      </v-container>
    </v-card-text>
    <Dialog
      :stateId="dialogId"
      @close="resetDialogs"
    >
      <ReviewVolumeDataDialog
        v-if="volumeInformationDialogOpen"
        :propVolumeData="chosenVolumeData"
        :propSpecies="allSpecies"
        :propGrades="allGrades"
        :propExtTicketNumber2="activeVolumeDataTicket"
        @close-dialog="resetDialogs"
        @reviewed="resetDialogs"
      />
      <ConfirmDialog
        v-if="confirmDialogOpen"
        :title="$t('closeDialogWithoutImporting')"
        :body="$t('closeImportDialogWithoutImporting')"
        :confirmText="$t('close')"
        width="20vw"
        @cancel="resetDialogs()"
        @confirm="closeImportingDialog()"
      />
    </Dialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import TicketHeaders from '@/headers/Ticket'
import IconHelpers from '@/utils/IconHelpers'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { ScalingDataImportProgress, ScaleSheetTicketType, ContractType } from '@/utils/Enumerations.js'
import { groupBy } from 'lodash'
import moment from 'moment'

const SHOW_ALL = 0
const GROUP_BY_DESTINATION = 1
const GROUP_BY_TRACT = 2
const GROUP_BY_ACCOUNT = 3
const GROUP_BY_SETTING = 4
const GROUP_BY_CONTRACT = 5
const GROUP_BY_FROM_ACCOUNT = 6
export default {
  name: 'ReviewImportDataDialog',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    FormWrapper: () => import('@/components/core/FormWrapper.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue'),
    ReviewVolumeDataDialog: () => import('@/components/ticket/ticket-details/scale-data/ReviewVolumeDataDialog.vue')
  },

  props: {
    propImportData: {
      type: Array,
      required: true
    }
  },

  data: () => ({
    openDestinationPanel: null,
    expandedSegments: [],
    columnSet: null,
    volumeInformationDialogOpen: false,
    confirmDialogOpen: false,
    chosenVolumeData: null,
    dialogId: uniqueDialogId('review-import-data-dialog'),
    importDataMapped: [],
    activeVolumeDataTicket: undefined,
    iconHelpers: IconHelpers,
    reviewConfirmed: false,
    importingInProgress: false,
    importPercentage: 0,
    ScalingDataImportProgress,
    ScaleSheetTicketType,
    search: '',
    currentDisplayMode: GROUP_BY_CONTRACT,
    importErrors: [],
    importErrorsDialogOpen: false,
    currentTab: 0,
    columnSetLYS: null,
    currentDisplayModeLYS: GROUP_BY_CONTRACT,
    openLYSPanel: null
  }),

  async created () {
    this.columnSet = this.reviewImportScaleDataDialogHeaders.getColumns()
    this.columnSetLYS = this.reviewImportLogYardSaleScaleDataDialogHeaders.getColumns()
    this.importDataMapped = this.propImportData.map(d => ({
      ...d,
      extTicketNumber2: d.ticket.extTicketNumber2,
      netWeight: d.ticket.inWeight - d.ticket.outWeight,
      reviewed: false,
      ticketNumber: d.ticket.ticketNumber,
      status: this.ScalingDataImportProgress.InProgress.value
    }))
    this.currentTab = this.logsImportData.length > 0 ? 0 : 1
  },

  computed: {
    ...mapGetters('species', ['allSpecies']),
    ...mapGetters('grade', ['allGrades']),
    title () {
      return !this.importingInProgress ? this.$t('reviewImportData') : this.$t('importingDataPleaseWait')
    },
    reviewImportScaleDataDialogHeaders () {
      return TicketHeaders.reviewImportScaleDataDialogHeaders()
    },
    reviewImportLogYardSaleScaleDataDialogHeaders () {
      return TicketHeaders.reviewImportLogYardSaleScaleDataDialogHeaders()
    },
    logsImportData () {
      return this.importDataMapped.filter(d => d.contractType === ContractType.Production.value || d.contractType === ContractType.WoodsSale.value)
    },
    logYardSaleImportData () {
      return this.importDataMapped.filter(d => d.contractType === ContractType.LogYardSale.value)
    },
    logsImportDataGroupedBySelection () {
      if (this.groupByTract) {
        return this.search
          ? Object.entries(groupBy(this.logsImportData, 'tract')).filter(g => g[0].toLowerCase().includes(this.search.toLowerCase()))
          : Object.entries(groupBy(this.logsImportData, 'tract'))
      } else if (this.groupByDestination) {
        return this.search
          ? Object.entries(groupBy(this.logsImportData, 'destination')).filter(g => g[0].toLowerCase().includes(this.search.toLowerCase()))
          : Object.entries(groupBy(this.logsImportData, 'destination'))
      } else if (this.groupByAccount) {
        return this.search
          ? Object.entries(groupBy(this.logsImportData, 'account')).filter(g => g[0].toLowerCase().includes(this.search.toLowerCase()))
          : Object.entries(groupBy(this.logsImportData, 'account'))
      } else if (this.groupByContract) {
        return this.search
          ? Object.entries(groupBy(this.logsImportData, 'contractId')).filter(g => g[1][0].tract.toLowerCase().includes(this.search.toLowerCase()) ||
          g[1][0].destination.toLowerCase().includes(this.search.toLowerCase()) ||
          g[1][0].account.toLowerCase().includes(this.search.toLowerCase()) ||
          g[1][0].setting.toLowerCase().includes(this.search.toLowerCase()))
          : Object.entries(groupBy(this.logsImportData, 'contractId'))
      } else if (this.groupBySetting) {
        return this.search
          ? Object.entries(groupBy(this.logsImportData, 'setting')).filter(g => g[0].toLowerCase().includes(this.search.toLowerCase()))
          : Object.entries(groupBy(this.logsImportData, 'setting'))
      } else {
        return []
      }
    },
    lysImportDataGroupedBySelection () {
      if (this.lysGroupByFromAccount) {
        return this.search
          ? Object.entries(groupBy(this.logYardSaleImportData, 'fromAccount')).filter(g => g[0].toLowerCase().includes(this.search.toLowerCase()))
          : Object.entries(groupBy(this.logYardSaleImportData, 'fromAccount'))
      } else if (this.lysGroupByDestination) {
        return this.search
          ? Object.entries(groupBy(this.logYardSaleImportData, 'destination')).filter(g => g[0].toLowerCase().includes(this.search.toLowerCase()))
          : Object.entries(groupBy(this.logYardSaleImportData, 'destination'))
      } else if (this.lysGroupByAccount) {
        return this.search
          ? Object.entries(groupBy(this.logYardSaleImportData, 'account')).filter(g => g[0].toLowerCase().includes(this.search.toLowerCase()))
          : Object.entries(groupBy(this.logYardSaleImportData, 'account'))
      } else if (this.lysGroupByContract) {
        return this.search
          ? Object.entries(groupBy(this.logYardSaleImportData, 'contractId')).filter(g =>
            g[1][0].fromAccount.toLowerCase().includes(this.search.toLowerCase()) ||
            g[1][0].account.toLowerCase().includes(this.search.toLowerCase()) ||
            g[1][0].destination.toLowerCase().includes(this.search.toLowerCase()))
          : Object.entries(groupBy(this.logYardSaleImportData, 'contractId'))
      } else {
        return []
      }
    },
    logYardSaleImportDataGroupedBySelection () {
      return this.search
        ? Object.entries(groupBy(this.logYardSaleImportData, 'contractId')).filter(g => g[1][0].fromAccount.toLowerCase().includes(this.search.toLowerCase()) ||
          g[1][0].account.toLowerCase().includes(this.search.toLowerCase()) ||
          g[1][0].destination.toLowerCase().includes(this.search.toLowerCase()))
        : Object.entries(groupBy(this.logYardSaleImportData, 'contractId'))
    },
    groupingIcons () {
      return [
        { name: 'groupByContract', icon: 'mdi-altimeter', active: this.groupByContract, value: GROUP_BY_CONTRACT },
        { name: 'groupByTract', icon: 'mdi-crosshairs-gps', active: this.groupByTract, value: GROUP_BY_TRACT },
        { name: 'groupByAccount', icon: 'mdi-account', active: this.groupByAccount, value: GROUP_BY_ACCOUNT },
        { name: 'groupByDestination', icon: 'mdi-domain', active: this.groupByDestination, value: GROUP_BY_DESTINATION },
        { name: 'groupBySetting', icon: 'mdi-map-marker-circle', active: this.groupBySetting, value: GROUP_BY_SETTING }
      ]
    },
    lysGroupingIcons () {
      return [
        { name: 'groupByContract', icon: 'mdi-altimeter', active: this.lysGroupByContract, value: GROUP_BY_CONTRACT },
        { name: 'groupByFromAccount', icon: 'mdi-account-arrow-right', active: this.lysGroupByFromAccount, value: GROUP_BY_FROM_ACCOUNT },
        { name: 'groupByAccount', icon: 'mdi-account', active: this.lysGroupByAccount, value: GROUP_BY_ACCOUNT },
        { name: 'groupByDestination', icon: 'mdi-domain', active: this.lysGroupByDestination, value: GROUP_BY_DESTINATION }
      ]
    },
    contractKeys () {
      return [
        { name: 'tract', icon: 'mdi-crosshairs-gps' },
        { name: 'account', icon: 'mdi-account' },
        { name: 'destination', icon: 'mdi-domain' },
        { name: 'setting', icon: 'mdi-map-marker-circle' }
      ]
    },
    lysContractKeys () {
      return [
        { name: 'fromAccount', icon: 'mdi-account-arrow-right' },
        { name: 'account', icon: 'mdi-account' },
        { name: 'destination', icon: 'mdi-domain' }
      ]
    },
    customColumns () {
      return [
        {
          slotName: 'extTicketNumber2',
          value: 'extTicketNumber2'
        },
        {
          slotName: 'tract',
          value: 'tract'
        },
        {
          slotName: 'destination',
          value: 'destination'
        },
        {
          slotName: 'setting',
          value: 'setting'
        },
        {
          slotName: 'netWeight',
          value: 'netWeight'
        },
        {
          slotName: 'actions',
          value: 'actions'
        }
      ]
    },
    customColumnsLYS () {
      return [
        { slotName: 'ticketNumber', value: 'ticketNumber' },
        { slotName: 'fromAccount', value: 'fromAccount' },
        { slotName: 'account', value: 'account' },
        { slotName: 'destination', value: 'destination' },
        { slotName: 'netWeight', value: 'netWeight' },
        { slotName: 'actions', value: 'actions' }
      ]
    },
    customColumnsBySelectionLYS () {
      if (this.lysGroupByFromAccount) {
        return this.customColumnsLYS.filter(c => c.value !== 'fromAccount')
      } else if (this.lysGroupByDestination) {
        return this.customColumnsLYS.filter(c => c.value !== 'destination')
      } else if (this.lysGroupByAccount) {
        return this.customColumnsLYS.filter(c => c.value !== 'account')
      } else if (this.lysGroupByContract) {
        return this.customColumnsLYS.filter(c => c.value !== 'account' && c.value !== 'destination' && c.value !== 'fromAccount')
      } else {
        return this.customColumnsLYS
      }
    },
    columnSetLYSBySelection () {
      if (this.lysGroupByFromAccount) {
        return this.columnSetLYS ? this.columnSetLYS.filter(c => c.value !== 'fromAccount') : []
      } else if (this.lysGroupByDestination) {
        return this.columnSetLYS ? this.columnSetLYS.filter(c => c.value !== 'destination') : []
      } else if (this.lysGroupByAccount) {
        return this.columnSetLYS ? this.columnSetLYS.filter(c => c.value !== 'account') : []
      } else if (this.lysGroupByContract) {
        return this.columnSetLYS ? this.columnSetLYS.filter(c => c.value !== 'account' && c.value !== 'destination' && c.value !== 'fromAccount') : []
      } else {
        return this.columnSetLYS ?? []
      }
    },
    customColumnsBySelection () {
      if (this.groupByTract) {
        return this.customColumns.filter(c => c.value !== 'tract')
      } else if (this.groupByDestination) {
        return this.customColumns.filter(c => c.value !== 'destination')
      } else if (this.groupByAccount) {
        return this.customColumns.filter(c => c.value !== 'account')
      } else if (this.groupByContract) {
        return this.customColumns.filter(c => c.value !== 'account' && c.value !== 'destination' && c.value !== 'tract' && c.value !== 'setting')
      } else if (this.groupBySetting) {
        return this.customColumns.filter(c => c.value !== 'setting')
      } else {
        return this.customColumns
      }
    },
    columnSetBySelection () {
      if (this.groupByTract) {
        return this.columnSet.filter(c => c.value !== 'tract')
      } else if (this.groupByDestination) {
        return this.columnSet.filter(c => c.value !== 'destination')
      } else if (this.groupByAccount) {
        return this.columnSet.filter(c => c.value !== 'account')
      } else if (this.groupByContract) {
        return this.columnSet.filter(c => c.value !== 'account' && c.value !== 'destination' && c.value !== 'tract' && c.value !== 'setting')
      } else if (this.groupBySetting) {
        return this.columnSet.filter(c => c.value !== 'setting')
      } else {
        return this.columnSet
      }
    },
    importingData () {
      return this.importDataMapped.filter(d => d.importStatus !== ScalingDataImportProgress.Success.value)
    },
    groupByDestination () {
      return this.currentDisplayMode === GROUP_BY_DESTINATION
    },
    groupByTract () {
      return this.currentDisplayMode === GROUP_BY_TRACT
    },
    groupByAccount () {
      return this.currentDisplayMode === GROUP_BY_ACCOUNT
    },
    groupByContract () {
      return this.currentDisplayMode === GROUP_BY_CONTRACT
    },
    groupBySetting () {
      return this.currentDisplayMode === GROUP_BY_SETTING
    },
    lysGroupByFromAccount () {
      return this.currentDisplayModeLYS === GROUP_BY_FROM_ACCOUNT
    },
    lysGroupByDestination () {
      return this.currentDisplayModeLYS === GROUP_BY_DESTINATION
    },
    lysGroupByAccount () {
      return this.currentDisplayModeLYS === GROUP_BY_ACCOUNT
    },
    lysGroupByContract () {
      return this.currentDisplayModeLYS === GROUP_BY_CONTRACT
    }
  },

  methods: {
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapActions('ticket', ['createTicket']),
    ...mapActions('scale-data', ['createVolumeData']),
    async confirmButtonClicked () {
      this.reviewConfirmed = true
      await this.createTicketsAndVolumeData()
    },
    updateImportPercentage () {
      const total = this.importDataMapped.length
      const completed = this.importDataMapped.filter(d => d.status !== this.ScalingDataImportProgress.InProgress.value).length
      this.importPercentage = total === 0 ? 100 : Math.round((completed / total) * 100)
    },
    showScaleData (importData) {
      this.volumeInformationDialogOpen = true
      this.chosenVolumeData = importData.volumeData
      importData.reviewed = true
      this.activeVolumeDataTicket = importData.contractType === ContractType.LogYardSale.value ? Number(importData.ticket.ticketNumber) : Number(importData.ticket.extTicketNumber2)
      this.openOrUpdateDialog({ id: this.dialogId, width: '80vw' })
    },
    resetDialogs () {
      this.volumeInformationDialogOpen = false
      this.chosenVolumeData = null
      this.confirmDialogOpen = false
      this.importErrorsDialogOpen = false
      this.importErrors = []
      this.closeDialogsAtOrAbove(this.dialogId)
    },
    askForConfirmation () {
      this.confirmDialogOpen = true
      this.openOrUpdateDialog({ id: this.dialogId, width: '20vw' })
    },
    closeImportingDialog () {
      this.confirmDialogOpen = false
      this.$emit('close')
      this.resetDialogs()
    },
    closeErrorsDialog () {
      this.resetDialogs()
      this.$emit('dataImportComplete')
    },
    handleGroupChange (mode) {
      if (this.currentDisplayMode === mode) {
        this.currentDisplayMode = SHOW_ALL
      } else {
        this.currentDisplayMode = mode
      }
    },
    handleGroupChangeLYS (mode) {
      if (this.currentDisplayModeLYS === mode) {
        this.currentDisplayModeLYS = SHOW_ALL
      } else {
        this.currentDisplayModeLYS = mode
      }
    },
    async createTicketsAndVolumeData () {
      this.importingInProgress = true
      if (this.importDataMapped === undefined || this.importDataMapped.length === 0) {
        this.setSnackError(this.$t('noValidScaleData'))
        this.resetDialogs()
        return
      }
      const ticketFailures = []
      const ticketSuccesses = []
      const sortedImportData = [...this.importDataMapped].sort((a, b) => {
        if (a.ticketType.value === ScaleSheetTicketType.Expanded.value && b.ticketType.value !== ScaleSheetTicketType.Expanded.value) return 1
        else if (a.ticketType.value !== ScaleSheetTicketType.Expanded.value && b.ticketType.value === ScaleSheetTicketType.Expanded.value) return -1
        else if (moment.utc(a.ticket.weighedOutAt).isBefore(moment.utc(b.ticket.weighedOutAt))) return -1
        else if (moment.utc(a.ticket.weighedOutAt).isAfter(moment.utc(b.ticket.weighedOutAt))) return 1
        return a.extTicketNumber2 - b.extTicketNumber2
      })
      for (const importData of sortedImportData) {
        let ticketSucceeded = false
        let ticket
        try {
          ticket = importData.alreadyCreated ? importData.ticket : await this.createTicket(importData.ticket)
          ticketSucceeded = true
          if (importData.ticketType.value === ScaleSheetTicketType.IncludesVolume.value) {
            importData.volumeData.ticketId = ticket.ticketId
            await this.createVolumeData({ volumeData: importData.volumeData })
          }
          importData.status = this.ScalingDataImportProgress.Success.value
          ticketSuccesses.push({ importData })
        } catch (e) {
          ticketFailures.push({
            ticketNumber: importData.contractType === ContractType.LogYardSale.value ? importData.ticket?.ticketNumber ?? '' : importData.extTicketNumber2 ?? '',
            importData,
            ticketSucceeded,
            error: e.response?.data?.message ?? e.message ?? 'Unknown error'
          })
          importData.status = ticketSucceeded ? this.ScalingDataImportProgress.PartialSuccess.value : this.ScalingDataImportProgress.Failed.value
        }
        this.updateImportPercentage()
      }

      const summaryData = this.buildSummaryData(ticketSuccesses, ticketFailures)

      if (ticketFailures.length > 0) {
        if (ticketFailures.length === this.importDataMapped.length) {
          this.setSnackError(this.$t('scaleDataImportFailed'))
        } else {
          this.setSnack(this.$t('scaleDataImportPartialSuccess', { count: this.importDataMapped.length - ticketFailures.length }))
        }
      } else {
        this.setSnack(this.$t('scaleDataImportSuccess', { count: this.importDataMapped.length }))
      }
      this.$emit('dataImportSummary', summaryData)
    },

    buildSummaryData (ticketSuccesses, ticketFailures) {
      const successfulTickets = ticketSuccesses.map(s => {
        const d = s.importData
        const grossVolume = d.volumeData?.segmentVolumes?.reduce((acc, sv) => acc + (sv.grossVolume ?? 0), 0) ?? 0
        const netVolume = d.volumeData?.segmentVolumes?.reduce((acc, sv) => acc + (sv.netVolume ?? 0), 0) ?? 0
        return {
          ticketNumber: d.contractType === ContractType.LogYardSale.value ? d.ticketNumber : d.extTicketNumber2,
          contractType: d.contractType,
          alreadyCreated: d.alreadyCreated,
          ticketType: d.ticketType,
          grossVolume,
          netVolume
        }
      })
      const failedTickets = ticketFailures.map(f => {
        const d = f.importData
        const grossVolume = d.volumeData?.segmentVolumes?.reduce((acc, sv) => acc + (sv.grossVolume ?? 0), 0) ?? 0
        const netVolume = d.volumeData?.segmentVolumes?.reduce((acc, sv) => acc + (sv.netVolume ?? 0), 0) ?? 0
        return {
          ticketNumber: f.ticketNumber,
          contractType: d.contractType,
          alreadyCreated: d.alreadyCreated,
          ticketType: d.ticketType,
          ticketSucceeded: f.ticketSucceeded,
          error: f.error,
          grossVolume,
          netVolume
        }
      })
      return {
        successfulTickets,
        failedTickets,
        totalGrossVolume: successfulTickets.reduce((acc, t) => acc + t.grossVolume, 0),
        totalNetVolume: successfulTickets.reduce((acc, t) => acc + t.netVolume, 0),
        failedGrossVolume: failedTickets.reduce((acc, t) => acc + t.grossVolume, 0),
        failedNetVolume: failedTickets.reduce((acc, t) => acc + t.netVolume, 0)
      }
    }
  }
}
</script>
